import type { FC } from 'react'

import { F0Box, F0Card, F0Icon, F0Text } from '@factorialco/f0-react'
import { Calendar, EyeVisible } from '@factorialco/f0-react/icons/app'
import { parseISO } from 'date-fns'
import {
  PastFeedbackEntryFragment,
  PastFeedbacksSidepanelDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

const EmptyState = () => (
  <F0Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap="sm"
    padding="xl"
  >
    <F0Text variant="body" content={i18n.t('performance_feedbacks.empty_state.no_feedback')} />
    <F0Text variant="description" content={i18n.t('performance_feedbacks.empty_state.title')} />
  </F0Box>
)

const LoadingState = () => (
  <F0Box display="flex" flexDirection="column" gap="md">
    <F0Card.Skeleton key={`goals-skeleton-1`} compact />
    <F0Card.Skeleton key={`goals-skeleton-2`} compact />
  </F0Box>
)

const VISIBILITY_LABELS: Record<string, () => string> = {
  private: () => i18n.t('performance_feedbacks.feedback_entry.private'),
  manager_only: () => i18n.t('performance_feedbacks.feedback_entry.manager_only'),
  private_and_manager: () => i18n.t('performance_feedbacks.feedback_entry.private_and_manager'),
}

const FeedbackCard: FC<{ entry: PastFeedbackEntryFragment }> = ({ entry }) => {
  const date = parseISO(entry.createdAt)

  return (
    <F0Card
      avatar={{
        type: 'person',
        firstName: entry.author.firstName,
        lastName: entry.author.lastName,
        src: buildUrl(entry.author.avatar) || '',
      }}
      title={entry.author.fullName}
      compact
    >
      <F0Box display="flex" flexDirection="column" gap="md">
        <F0Text variant="body" content={entry.feedback} />

        <F0Box display="flex" flexDirection="row" gap="md" alignItems="center">
          <F0Box display="flex" flexDirection="row" gap="xs" alignItems="center">
            <F0Icon icon={Calendar} color="secondary" />

            <F0Text variant="body" content={i18nDateToString(date, 'ui_abbreviated_month_year')} />
          </F0Box>

          <F0Box display="flex" flexDirection="row" gap="xs" alignItems="center">
            <F0Icon icon={EyeVisible} color="secondary" />

            <F0Text
              variant="body"
              content={VISIBILITY_LABELS[entry.visibility]?.() ?? entry.visibility}
            />
          </F0Box>
        </F0Box>
      </F0Box>
    </F0Card>
  )
}

export const PastFeedbacksTab: FC<{ targetAccessId: string }> = ({ targetAccessId }) => {
  const { data, loading } = useQuery(PastFeedbacksSidepanelDocument, {
    variables: { receiverId: targetAccessId },
  })

  const entries = data?.feedbacks.feedbackEntriesConnection?.edges.map((edge) => edge.node) ?? []

  if (loading) {
    return <LoadingState />
  }

  if (!entries.length) {
    return <EmptyState />
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="md">
      {entries.map((entry) => (
        <FeedbackCard key={entry.id} entry={entry} />
      ))}
    </F0Box>
  )
}
